import { useSearchParams } from "react-router-dom";
import CLO from "../assets/clo.svg";
import CLOSET from "../assets/closet.svg";
import MD from "../assets/md.svg";
import CONNECT from "../assets/closet_connect.svg";
import { Brand, Page } from "../context/AppProvider";
import { useAppContext } from "../hooks/useAppContext";
import { AxiosError } from "axios";
import useBoundStore from "../store/useBoundStore";
import { getTranslation } from "../locales/getTranslation";
import { useEffect, useRef } from "react";

type ModalBrandProps = {
  className?: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  triggerRef?: React.RefObject<HTMLElement>;
};

const ModalBrand = ({
  className,
  showModal,
  setShowModal,
  triggerRef,
}: ModalBrandProps) => {
  const { brand, setBrand, locale } = useAppContext();
  const lastQuestion = useBoundStore((state) => state.lastQuestion);
  const setLastQuestion = useBoundStore((state) => state.setLastQuestion);
  const setAnswers = useBoundStore((state) => state.setAnswer);
  const setConversationID = useBoundStore((state) => state.setId);
  const isLoading = useBoundStore((state) => state.isLoading);
  const error = useBoundStore((state) => state.error);
  const setError = useBoundStore((state) => state.setError);
  const setPage = useBoundStore((state) => state.setPage);
  const setCitation = useBoundStore((state) => state.setCitation);
  const [searchParams, setSearchParams] = useSearchParams();
  const modalRef = useRef<HTMLUListElement>(null);

  const updateQueryParams = (brand: Brand) => {
    const { search } = window.location;
    const searchParamsLocale = new URLSearchParams(search).get("locale");

    const newQueryParameters: URLSearchParams = new URLSearchParams();
    newQueryParameters.set("brand", brand.toLowerCase());

    if (searchParamsLocale) {
      newQueryParameters.set("locale", searchParamsLocale);
    }

    setSearchParams(newQueryParameters);
  };

  const clearChat = () => {
    if (lastQuestion && !isLoading) {
      setLastQuestion("");
      error && setError({} as AxiosError);
      setCitation(undefined);
      setAnswers([]);
      setPage(Page.HOME);
      setConversationID("");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (!showModal || !triggerRef?.current) return;

      // 1. If clicking inside modal, stay open
      if (modalRef.current?.contains(target)) return;

      // 2. If clicking the trigger, let the BrandIcon's onClick handle it
      // We return here so we don't call setShowModal(false)
      // right before the icon calls setShowModal(true)
      if (triggerRef.current?.contains(target)) return;

      setShowModal(false);
    };

    // Change "mousedown" to "click"
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showModal]);

  return (
    <ul
      ref={modalRef}
      className={`${className} pointer-events-auto flex w-full flex-col items-start justify-center gap-1 rounded-lg bg-grayscale-solid-elevate-level1 p-2 shadow md:w-[350px]`}
    >
      {Object.keys(Brand).map((key) => {
        if (import.meta.env.MODE === "prod") {
          if (key === Brand.ALLINONE || key === Brand.CLOSET_CONNECT) {
            return null;
          }
        }

        const brandName = Brand[key as keyof typeof Brand];

        return (
          <li
            key={key}
            className={`group flex w-full cursor-pointer items-center justify-start gap-4 rounded-xl p-1 hover:bg-blue-solid-blue1 sm:rounded-[14px] ${brandName === brand ? "bg-blue-solid-blue1" : ""}`}
            onClick={() => {
              clearChat();
              updateQueryParams(brandName);
              setBrand(brandName);
              setShowModal(false);
            }}
          >
            <img
              className="size-10"
              src={
                brandName === Brand.CLO3D
                  ? CLO
                  : brandName === Brand.CLOSET
                    ? CLOSET
                    : brandName === Brand.CLOSET_CONNECT
                      ? CONNECT
                      : brandName === Brand.MD
                        ? MD
                        : CLO
              }
              alt={brandName}
            />
            <div className="flex flex-col justify-center h-full">
              <h6
                className={`${brandName === brand ? "text-blue-solid-blue6" : ""} text-xs font-bold leading-[17px] text-grayscale-solid-g10 sm:text-base md:leading-snug`}
              >
                {brandName === Brand.CLO3D
                  ? "CLO"
                  : brandName === Brand.CLOSET
                    ? "CLO-SET"
                    : brandName === Brand.CLOSET_CONNECT
                      ? "CONNECT"
                      : brandName === Brand.MD
                        ? "Marvelous Designer"
                        : brandName}
              </h6>
              <p
                className={`${brandName === brand ? "text-blue-solid-blue6" : ""} text-[10px] font-normal leading-[14px] text-grayscale-solid-g10 sm:text-xs sm:leading-[17px]`}
              >
                {getTranslation(
                  locale,
                  `${
                    brandName === Brand.CLOSET_CONNECT
                      ? "CONNECT"
                      : brandName === Brand.MD
                        ? "Marvelous Designer"
                        : brandName
                  } Description`,
                )}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default ModalBrand;
